/**
 * ExpenseInvoiceStatusBadge Component
 * Displays visual status indicator for expense invoices
 */

import React from 'react';
import { Chip } from '@mui/material';
import {
  DraftsOutlined,
  MailOutline,
  VerifiedOutlined,
  PaidOutlined,
  CancelOutlined,
} from '@mui/icons-material';
import { ExpenseInvoiceStatus } from '../Types/expenseInvoice.types';
import { EXPENSE_INVOICE_STATUS_LABELS } from '../Utils/expenseInvoiceStatusLabels';

interface ExpenseInvoiceStatusBadgeProps {
  status: ExpenseInvoiceStatus;
  size?: 'small' | 'medium';
}

/**
 * Status configuration mapping
 * Backend enum: Draft, Received, Verified, Paid, Cancelled
 */
const STATUS_CONFIG: Record<
  ExpenseInvoiceStatus,
  {
    label: string;
    color: 'default' | 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning';
    icon: React.ReactElement;
  }
> = {
  [ExpenseInvoiceStatus.Draft]: {
    label: EXPENSE_INVOICE_STATUS_LABELS[ExpenseInvoiceStatus.Draft],
    color: 'default',
    icon: <DraftsOutlined fontSize="small" />,
  },
  [ExpenseInvoiceStatus.Received]: {
    label: EXPENSE_INVOICE_STATUS_LABELS[ExpenseInvoiceStatus.Received],
    color: 'info',
    icon: <MailOutline fontSize="small" />,
  },
  [ExpenseInvoiceStatus.Verified]: {
    label: EXPENSE_INVOICE_STATUS_LABELS[ExpenseInvoiceStatus.Verified],
    color: 'primary',
    icon: <VerifiedOutlined fontSize="small" />,
  },
  [ExpenseInvoiceStatus.Paid]: {
    label: EXPENSE_INVOICE_STATUS_LABELS[ExpenseInvoiceStatus.Paid],
    color: 'success',
    icon: <PaidOutlined fontSize="small" />,
  },
  [ExpenseInvoiceStatus.Cancelled]: {
    label: EXPENSE_INVOICE_STATUS_LABELS[ExpenseInvoiceStatus.Cancelled],
    color: 'error',
    icon: <CancelOutlined fontSize="small" />,
  },
};

const ExpenseInvoiceStatusBadge: React.FC<ExpenseInvoiceStatusBadgeProps> = ({
  status,
  size = 'small',
}) => {
  const config = STATUS_CONFIG[status];

  if (!config) {
    return <Chip label="Nieznany" color="default" size={size} variant="outlined" />;
  }

  return (
    <Chip
      label={config.label}
      color={config.color}
      size={size}
      icon={config.icon}
      variant="outlined"
    />
  );
};

export default ExpenseInvoiceStatusBadge;
